import React, { ReactNode, useCallback, useEffect, useState } from 'react';

import { AppService } from './app.types';
import { useAppData } from '../../hooks/queries/appSession.queries';

export interface AppConfig {
  appName: string;
  title: string;
}

interface ExtendedAppService extends AppService {
  config: AppConfig;
  setConfig: (config: Partial<AppConfig>) => void;
}

export const DEFAULT_APP_CONFIG: AppConfig = {
  appName: 'myTopcon',
  title: 'myTopcon',
};

export const AppContext = React.createContext<ExtendedAppService | null>(null);

interface Props {
  children: ReactNode;
}

export const AppProvider: React.FC<Props> = ({ children }) => {
  const appService = useAppData();
  const [config, setConfigState] = useState<AppConfig>({ ...DEFAULT_APP_CONFIG });

  const setConfig = useCallback((newConfig: Partial<AppConfig>) => {
    setConfigState((prev) => {
      const hasAppNameChanged =
        newConfig.appName !== undefined && newConfig.appName !== prev.appName;
      const hasTitleChanged = newConfig.title !== undefined && newConfig.title !== prev.title;

      const hasChanged = hasAppNameChanged || hasTitleChanged;

      if (!hasChanged) return prev;

      return { ...prev, ...newConfig };
    });
  }, []);

  useEffect(() => {
    document.title = config.title;
  }, [config.title]);

  const extendedAppService: ExtendedAppService = {
    ...appService,
    config,
    setConfig,
  };

  return <AppContext.Provider value={extendedAppService}>{children}</AppContext.Provider>;
};

export function useAppService() {
  const appService = React.useContext(AppContext);
  if (!appService) throw new Error('useAppService must be used within AppProvider');
  return appService;
}
